"use client";

import { useTranslations } from "next-intl";
import { ChevronLeft, User } from "lucide-react";
import Image from "next/image";
import { useEffect, useLayoutEffect, useRef, useState, type ReactNode, type RefObject } from "react";
import { cn } from "@/lib/utils";
import { getAvatarUrl } from "@/lib/getAvatarUrl";
import { SQUARE_SM, imagePresetUrl } from "@/lib/images/presets";
import {
  CHAT_CONVERSATION_HEADER_LAYER_CLASSNAME,
  CHAT_CONVERSATION_HEADER_SAFE_TOP_CLASS,
} from "@/components/chat/chat-conversation-chrome-styles";
import { HUB_OVERLAY_BACKDROP, HUB_OVERLAY_MASK } from "@/components/hub/hub-overlay-backdrop";

export const CHAT_STICKY_HEADER_HEIGHT_VAR = "--chat-sticky-header-height";
export const CHAT_STICKY_HEADER_ROW_HEIGHT = "h-11";
export const CHAT_STICKY_HEADER_OFFSET = "pt-[var(--chat-sticky-header-height,2.75rem)]";

export const CHAT_INBOX_HEADER_CHROME =
  "sticky top-0 z-20 shrink-0 border-b border-border bg-background/95 backdrop-blur-sm supports-backdrop-filter:bg-background/80";

export const CHAT_CONVERSATION_STICKY_CHROME = CHAT_CONVERSATION_HEADER_LAYER_CLASSNAME;

/** @deprecated Use CHAT_INBOX_HEADER_CHROME or CHAT_CONVERSATION_STICKY_CHROME */
export const CHAT_PANEL_STICKY_CHROME = CHAT_INBOX_HEADER_CHROME;

export const chatStickyIconButtonClass =
  "h-10 w-10 shrink-0 rounded-full active:scale-95 transition-all inline-flex items-center justify-center hover:bg-secondary/50";

export function chatStickyHeaderPaddingTop(fallback = "2.75rem") {
  return `var(${CHAT_STICKY_HEADER_HEIGHT_VAR}, ${fallback})`;
}

export function useChatStickyHeaderHeight(
  headerRef: RefObject<HTMLElement | null>,
  enabled = true,
) {
  useLayoutEffect(() => {
    if (!enabled) return;
    const el = headerRef.current;
    if (!el) return;
    const root = document.documentElement;
    const update = () => {
      root.style.setProperty(
        CHAT_STICKY_HEADER_HEIGHT_VAR,
        `${Math.ceil(el.getBoundingClientRect().height)}px`,
      );
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    window.addEventListener("resize", update, { passive: true });
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", update);
      root.style.removeProperty(CHAT_STICKY_HEADER_HEIGHT_VAR);
    };
  }, [enabled, headerRef]);
}

export function useChatHeaderChromeActive(
  scrollContainerRef: RefObject<HTMLElement | null> | undefined,
  forceActive = false,
) {
  const [scrollActive, setScrollActive] = useState(false);

  useEffect(() => {
    const el = scrollContainerRef?.current;
    if (!el) return;
    const onScroll = () => {
      setScrollActive(el.scrollTop > 8);
    };
    onScroll();
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, [scrollContainerRef]);

  return forceActive || scrollActive;
}

export function chatStickyChromeIconButtonClass(chromeActive: boolean, showBackdrop = true) {
  return cn(
    chatStickyIconButtonClass,
    showBackdrop && !chromeActive && "bg-background/30 backdrop-blur-md hover:bg-background/50",
  );
}

export function ChatHeaderBackdrop({
  active,
  className,
}: {
  active: boolean;
  className?: string;
}) {
  return (
    <div
      aria-hidden
      className={cn(
        HUB_OVERLAY_BACKDROP,
        "transition-opacity duration-300",
        active ? "opacity-100" : "opacity-0",
        className,
      )}
      style={HUB_OVERLAY_MASK}
    />
  );
}

type ChatStickyHeaderProps = {
  title: string;
  avatarUrl?: string | null;
  showParticipantChrome?: boolean;
  onBack?: () => void;
  backLabel?: string;
  trailing?: ReactNode;
  className?: string;
  position?: "fixed" | "absolute";
  showBackdrop?: boolean;
  surface?: "overlay" | "panel";
  maxWidthClassName?: string;
  measureHeight?: boolean;
  scrollContainerRef?: RefObject<HTMLElement | null>;
};

export function ChatStickyHeader({
  title,
  avatarUrl,
  showParticipantChrome: showParticipantChromeProp,
  onBack,
  backLabel,
  trailing,
  className,
  position = "fixed",
  showBackdrop = true,
  surface = "overlay",
  maxWidthClassName = "max-w-5xl",
  measureHeight = true,
  scrollContainerRef,
}: ChatStickyHeaderProps) {
  const tInfo = useTranslations("profile.info");
  const headerRef = useRef<HTMLElement>(null);
  const resolvedBackLabel = backLabel ?? tInfo("goBack");
  const resolvedAvatarUrl = getAvatarUrl(avatarUrl || "") || null;
  const showParticipantChrome =
    showParticipantChromeProp ?? (avatarUrl !== undefined || title.length > 0);
  useChatStickyHeaderHeight(headerRef, measureHeight);
  const chromeActive = useChatHeaderChromeActive(scrollContainerRef);
  const backdropActive = showBackdrop && (chromeActive || showParticipantChrome);

  const isPanel = surface === "panel";
  const layerPosition = isPanel ? "absolute" : position;
  const safeTopClass = isPanel ? "" : CHAT_CONVERSATION_HEADER_SAFE_TOP_CLASS;
  const iconButtonClass = chatStickyChromeIconButtonClass(chromeActive, showBackdrop);

  const row = (
    <div
      className={cn(
        "mx-auto flex w-full shrink-0 items-center gap-2 px-3 md:gap-3 md:px-4",
        showParticipantChrome
          ? "min-h-12 md:min-h-[4.5rem] md:py-1"
          : CHAT_STICKY_HEADER_ROW_HEIGHT,
        maxWidthClassName,
      )}
    >
      {onBack ? (
        <button
          type="button"
          onClick={onBack}
          className={iconButtonClass}
          aria-label={resolvedBackLabel}
        >
          <ChevronLeft className="h-5 w-5 text-foreground" />
        </button>
      ) : null}

      {showParticipantChrome ? (
        <div className="flex min-w-0 flex-1 items-center gap-3 md:gap-4">
          <div className="relative flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-full bg-muted ring-1 ring-border/50 md:h-11 md:w-11">
            {resolvedAvatarUrl ? (
              <Image
                // 40 px mobile / 44 px desktop -> SQUARE_SM. `quality` dropped:
                // the preset owns it.
                src={imagePresetUrl(resolvedAvatarUrl, SQUARE_SM)}
                alt=""
                fill
                className="object-cover"
                sizes="(max-width: 768px) 40px, 44px"
              />
            ) : (
              <User className="h-5 w-5 text-muted-foreground md:h-6 md:w-6" />
            )}
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold leading-tight text-foreground md:text-base md:leading-tight">
              {title}
            </p>
          </div>
        </div>
      ) : (
        <div className="relative min-w-0 flex-1">
          <p className="truncate text-sm font-semibold leading-tight text-foreground">
            {title}
          </p>
        </div>
      )}

      {trailing}
    </div>
  );

  return (
    <header
      ref={headerRef}
      data-chat-sticky-header
      className={cn(
        CHAT_CONVERSATION_STICKY_CHROME,
        safeTopClass,
        layerPosition,
        isPanel ? "z-20" : "z-50",
        className,
      )}
    >
      {showBackdrop ? <ChatHeaderBackdrop active={backdropActive} /> : null}
      <div className="relative z-1">{row}</div>
    </header>
  );
}
